const API_BASE = '/api';

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include', // Include cookies for session auth
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || data.error || 'API request failed');
  }

  return data;
}

// Domain API
export const domainApi = {
  list: () => fetchApi<{ domains: Domain[] }>('/domains'),

  get: (id: string) => fetchApi<{ domain: Domain; sitemaps: Sitemap[] }>(`/domains/${id}`),

  create: (domain: string, urlBudget?: number) =>
    fetchApi<{ domain: Domain; instructions: DomainInstructions }>('/domains', {
      method: 'POST',
      body: JSON.stringify({ domain, url_budget: urlBudget }),
    }),

  verify: (id: string) => fetchApi<VerifyResponse>(`/domains/${id}/verify`, { method: 'POST' }),

  delete: (id: string) => fetchApi<{ success: boolean }>(`/domains/${id}`, { method: 'DELETE' }),

  updateBudget: (id: string, urlBudget: number) =>
    fetchApi<{ domain: Domain; pool: PoolStatus }>(`/domains/${id}/budget`, {
      method: 'PATCH',
      body: JSON.stringify({ url_budget: urlBudget }),
    }),

  // IndexNow API
  getIndexNowStatus: (id: string) =>
    fetchApi<{
      success: boolean;
      indexNow: {
        enabled: boolean;
        keyConfigured: boolean;
        key: string | null;
        keyFileUrl: string | null;
        targetDomain: string;
        targetKeyFileUrl: string | null;
      };
    }>(`/domains/${id}/indexnow`),

  verifyIndexNowKey: (id: string, targetDomain?: string) =>
    fetchApi<{
      success: boolean;
      verified: boolean;
      keyFileUrl: string;
      message: string;
    }>(`/domains/${id}/indexnow/verify`, {
      method: 'POST',
      body: JSON.stringify({ targetDomain }),
    }),

  enableIndexNow: (id: string) =>
    fetchApi<{ success: boolean; message: string; indexNow: { enabled: boolean; key: string; keyFileUrl: string; info: string } }>(`/domains/${id}/indexnow/enable`, {
      method: 'POST',
    }),

  regenerateIndexNowKey: (id: string) =>
    fetchApi<{ success: boolean; message: string; indexNow: { enabled: boolean; key: string; keyFileUrl: string } }>(`/domains/${id}/indexnow/regenerate`, {
      method: 'POST',
    }),

  disableIndexNow: (id: string) =>
    fetchApi<{ success: boolean; message: string }>(`/domains/${id}/indexnow`, {
      method: 'DELETE',
    }),

  // llms.txt co-hosting
  getLlmsTxt: (id: string) =>
    fetchApi<{
      success: boolean;
      llmsTxt: {
        content: string;
        isCustom: boolean;
        updatedAt: string | null;
        publishedUrl: string;
        mode: 'manual' | 'auto' | 'off';
        autoAt: string | null;
      };
    }>(`/domains/${id}/llms-txt`),

  updateLlmsTxt: (id: string, content: string) =>
    fetchApi<{
      success: boolean;
      llmsTxt: { content: string; isCustom: boolean; publishedUrl: string };
    }>(`/domains/${id}/llms-txt`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),

  resetLlmsTxt: (id: string) =>
    fetchApi<{ success: boolean; message: string }>(`/domains/${id}/llms-txt`, {
      method: 'DELETE',
    }),

  // llms.txt v2 — llms-full.txt, mode toggle, regenerate from sitemap.
  getLlmsFullTxt: (id: string) =>
    fetchApi<{
      success: boolean;
      llmsFullTxt: { content: string; isCustom: boolean; publishedUrl: string };
    }>(`/domains/${id}/llms-full-txt`),

  updateLlmsFullTxt: (id: string, content: string) =>
    fetchApi<{
      success: boolean;
      llmsFullTxt: { content: string; isCustom: boolean; publishedUrl: string };
    }>(`/domains/${id}/llms-full-txt`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),

  resetLlmsFullTxt: (id: string) =>
    fetchApi<{ success: boolean; message: string }>(`/domains/${id}/llms-full-txt`, {
      method: 'DELETE',
    }),

  setLlmsTxtMode: (id: string, mode: 'manual' | 'auto' | 'off') =>
    fetchApi<{ success: boolean; mode: string }>(`/domains/${id}/llms-txt/mode`, {
      method: 'PATCH',
      body: JSON.stringify({ mode }),
    }),

  regenerateLlmsTxt: (id: string) =>
    fetchApi<{
      success: boolean;
      stats: { urlsConsidered: number; llmsTxtBytes: number; llmsFullTxtBytes: number };
    }>(`/domains/${id}/llms-txt/regenerate`, { method: 'POST' }),

  // /.well-known/* file management — generic CRUD against the well_known_files table.
  listWellKnown: (id: string) =>
    fetchApi<{
      success: boolean;
      supported: { path: string; label: string; contentType: 'application/json' | 'text/plain' }[];
      files: {
        path: string;
        contentType: string;
        body: string;
        enabled: boolean;
        updatedAt: string;
        publishedUrl: string;
      }[];
    }>(`/domains/${id}/wellknown`),

  saveWellKnown: (id: string, body: { path: string; body: string; enabled?: boolean }) =>
    fetchApi<{
      success: boolean;
      file: { path: string; contentType: string; body: string; enabled: boolean; publishedUrl: string };
    }>(`/domains/${id}/wellknown`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteWellKnown: (id: string, path: string) =>
    fetchApi<{ success: boolean }>(`/domains/${id}/wellknown?path=${encodeURIComponent(path)}`, {
      method: 'DELETE',
    }),

  // robots.txt v2 — per-AI-bot directives + Content Signals.
  getRobotsDirectives: (id: string) =>
    fetchApi<{
      success: boolean;
      directives: {
        preset: 'allow-all' | 'block-training' | 'block-all' | 'custom';
        perBot: Record<string, 'allow' | 'disallow'>;
        contentSignals: Record<string, 'yes' | 'no'>;
        custom: string;
      } | null;
      bots: { userAgent: string; label: string; description: string; defaultRule: 'allow' | 'disallow' }[];
      contentSignalKinds: string[];
      preview: string;
      publishedUrl: string;
      proxyVerified: boolean;
    }>(`/domains/${id}/robots-directives`),

  saveRobotsDirectives: (
    id: string,
    body: {
      preset: 'allow-all' | 'block-training' | 'block-all' | 'custom';
      perBot: Record<string, 'allow' | 'disallow'>;
      contentSignals: Record<string, 'yes' | 'no'>;
      custom: string;
    }
  ) =>
    fetchApi<{ success: boolean; preview: string; proxyVerified: boolean }>(
      `/domains/${id}/robots-directives`,
      { method: 'PUT', body: JSON.stringify(body) }
    ),

  resetRobotsDirectives: (id: string) =>
    fetchApi<{ success: boolean; message: string }>(`/domains/${id}/robots-directives`, {
      method: 'DELETE',
    }),

  // Per-domain editor state: hreflang clusters + image metadata. Both are
  // opaque JSON blobs that round-trip the dashboard editor verbatim.
  getHreflang: (id: string) =>
    fetchApi<{ success: boolean; content: string }>(`/domains/${id}/hreflang`),
  saveHreflang: (id: string, content: string) =>
    fetchApi<{ success: boolean }>(`/domains/${id}/hreflang`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),
  getImageMetadata: (id: string) =>
    fetchApi<{ success: boolean; content: string }>(`/domains/${id}/image-metadata`),
  saveImageMetadata: (id: string, content: string) =>
    fetchApi<{ success: boolean }>(`/domains/${id}/image-metadata`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),

  // Apex-proxy: the snippet customers paste into their main-site host so the
  // well-known files we serve at sitemap.<their-domain> appear at their apex.
  getProxy: (id: string) =>
    fetchApi<{
      success: boolean;
      proxy: {
        hostKind: string | null;
        apexDomain: string | null;
        pathsEnabled: string[];
        verifiedAt: string | null;
        subdomain: string;
      };
    }>(`/domains/${id}/proxy`),

  saveProxy: (
    id: string,
    body: {
      hostKind: string | null;
      apexDomain: string | null;
      pathsEnabled: string[];
    }
  ) =>
    fetchApi<{ success: boolean }>(`/domains/${id}/proxy`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  verifyProxy: (id: string) =>
    fetchApi<{
      success: boolean;
      verified: boolean;
      verifiedAt: string | null;
      results: {
        path: string;
        probedPath: string;
        ok: boolean;
        reason?: string;
      }[];
    }>(`/domains/${id}/proxy/verify`, { method: 'POST' }),
};

// Pool API
export const poolApi = {
  status: () => fetchApi<{ pool: PoolStatus; limits: { maxDomains: number; minUrlsPerDomain: number } }>('/pool'),
};

// Sitemap API
export const sitemapApi = {
  generate: (data: GenerateSitemapRequest) =>
    fetchApi<GenerateSitemapResponse>('/sitemap/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  generateFromCsv: (domain: string, csv: string) =>
    fetchApi<GenerateSitemapResponse>('/sitemap/generate-from-csv', {
      method: 'POST',
      body: JSON.stringify({ domain, csv }),
    }),

  previewSegmentation: (data: {
    urls: { loc: string }[];
    segmentation?: SegmentationConfig;
    sitemapSize?: SitemapSizeTier;
  }) =>
    fetchApi<SegmentationPreviewResponse>('/sitemap/preview-segmentation', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  status: (domainId: string) => fetchApi<SitemapStatus>(`/sitemap/status/${domainId}`),

  ping: (domainId: string) =>
    fetchApi<{ sitemapUrl: string; results: PingResults }>(`/sitemap/ping/${domainId}`, {
      method: 'POST',
    }),

  submitToIndexNow: (domainId: string) =>
    fetchApi<{
      success: boolean;
      message: string;
      urlCount: number;
      totalUrlsInSitemaps: number;
      truncated: boolean;
      status: number;
      timestamp: string;
    }>(`/sitemap/indexnow/${domainId}`, {
      method: 'POST',
    }),
};

// User API
export const userApi = {
  me: () => fetchApi<UserInfo>('/me'),
};

// Admin API (server-gated to the admin account; a 403 is thrown for others)
export interface FunnelStats {
  funnel: {
    signups: number;
    addedDomain: number;
    verifiedDomain: number;
    generatedSitemap: number;
  };
  paying: {
    total: number;
    byPlan: Record<string, number>;
    estimatedMrrUsd: number;
  };
  signupsByDay: { date: string; count: number }[];
  events30d: Record<string, number>;
  generatedAt: string;
}

export const adminApi = {
  getFunnel: () => fetchApi<FunnelStats>('/admin/funnel'),
};

// Auth API
export const authApi = {
  me: () =>
    fetchApi<AuthInfo>('/auth/me').catch(() => ({
      authenticated: false,
      user: null,
      usage: null,
      limits: null,
      pool: null,
    })),

  logout: () => fetchApi<{ success: boolean }>('/auth/logout', { method: 'POST' }),

  getGoogleLoginUrl: () => '/api/auth/google',
};

// API Key API
export const apiKeyApi = {
  list: () => fetchApi<{ apiKeys: ApiKeyInfo[] }>('/api-keys'),

  create: (name?: string) =>
    fetchApi<{ apiKey: NewApiKey; warning: string }>('/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  revoke: (id: string) => fetchApi<{ success: boolean }>(`/api-keys/${id}`, { method: 'DELETE' }),
};

// Billing API
export interface SubscriptionInfo {
  plan: 'free' | 'starter' | 'pro' | 'business' | 'enterprise';
  status:
    | 'none'
    | 'active'
    | 'trialing'
    | 'past_due'
    | 'canceled'
    | 'incomplete'
    | 'incomplete_expired'
    | 'unpaid'
    | 'paused';
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  has_stripe_customer: boolean;
}

export const billingApi = {
  subscription: () => fetchApi<SubscriptionInfo>('/billing/subscription'),

  checkout: (plan: 'starter' | 'pro' | 'business', interval: 'month' | 'year' = 'month') =>
    fetchApi<{ url: string; id: string }>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan, interval }),
    }),

  portal: () =>
    fetchApi<{ url: string }>('/billing/portal', { method: 'POST' }),
};

// GSC API
export const gscApi = {
  status: () => fetchApi<GSCStatus>('/gsc/status'),

  getConnectUrl: () => '/api/gsc/connect',

  disconnect: () => fetchApi<{ success: boolean }>('/gsc/disconnect', { method: 'POST' }),

  sites: () => fetchApi<{ sites: GSCSite[] }>('/gsc/sites'),

  submit: (domainId: string) =>
    fetchApi<{ success: boolean; sitemapUrl: string }>(`/gsc/submit/${domainId}`, { method: 'POST' }),
};

// Types
export interface Domain {
  id: string;
  domain: string;
  verified: boolean;
  verifiedAt: string | null;
  sslStatus: string;
  targetCname: string;
  urlBudget: number;
  urlsUsed: number;
  createdAt: string;
  updatedAt?: string;
}

export interface DomainInstructions {
  step1: string;
  record: {
    type: string;
    name: string;
    target: string;
  };
  step2: string;
  step3: string;
}

export interface Sitemap {
  id: string;
  filename: string;
  urlCount: number;
  fileSize: number;
  isIndex: boolean;
  lastGooglePing: string | null;
  lastIndexNow: string | null;
  indexNowStatus: 'success' | 'failed' | 'pending' | null;
  gscStatus: 'pending' | 'success' | 'error' | 'not_connected' | null;
  gscSubmittedAt: string | null;
  gscError: string | null;
  createdAt: string;
}

export interface VerifyResponse {
  success: boolean;
  verified: boolean;
  expectedCname: string;
  actualCname: string | null;
  message: string;
}

export interface ImageEntry {
  loc: string;
  caption?: string;
  title?: string;
  geo_location?: string;
  license?: string;
}

export interface NewsEntry {
  publication: {
    name: string; // Publication name (e.g., "The New York Times")
    language: string; // ISO 639 language code (e.g., "en", "es")
  };
  title: string; // Article title
  publication_date: string; // ISO 8601 date (e.g., "2024-01-15T10:00:00Z")
  keywords?: string[]; // Optional keywords/tags
  stock_tickers?: string[]; // Optional stock symbols (e.g., ["NASDAQ:GOOG"])
}

export interface VideoEntry {
  thumbnail_loc: string; // URL to video thumbnail (required)
  title: string; // Video title (required, max 100 chars)
  description: string; // Video description (required, max 2048 chars)
  content_loc?: string; // URL to actual video file
  player_loc?: string; // URL to embeddable player
  duration?: number; // Duration in seconds
  expiration_date?: string; // When video expires (ISO 8601)
  rating?: number; // 0.0 to 5.0
  view_count?: number; // Number of views
  publication_date?: string; // When video was published (ISO 8601)
  family_friendly?: boolean; // Safe for all ages
  live?: boolean; // Is live stream
  tag?: string[]; // Tags (max 32)
  category?: string; // Video category
  uploader?: {
    name: string;
    info?: string; // URL to uploader info
  };
}

export type SitemapSizeTier = 'gsc-optimized' | 'balanced' | 'standard';
export type SitemapType = 'standard' | 'news' | 'video';
export type SegmentationStrategy = 'sequential' | 'directory';

export interface SegmentationConfig {
  strategy: SegmentationStrategy;
  depth?: number;
  minGroupSize?: number;
}

export interface SegmentationGroup {
  name: string;
  urlCount: number;
  sample: string[];
  estimatedSitemaps: number;
}

export interface SegmentationPreviewResponse {
  success: boolean;
  strategy: SegmentationStrategy;
  totalUrls: number;
  totalGroups: number;
  totalSitemaps: number;
  groups: SegmentationGroup[];
  sitemapSize: {
    tier: SitemapSizeTier;
    urlsPerSitemap: number;
  };
}

export interface GenerateSitemapRequest {
  domain: string;
  urls: {
    loc: string;
    lastmod?: string;
    changefreq?: string;
    priority?: number;
    images?: ImageEntry[];
    news?: NewsEntry;
    video?: VideoEntry;
    hreflang?: { lang: string; href: string }[];
  }[];
  pingGoogle?: boolean;
  sitemapName?: string;
  sitemapSize?: SitemapSizeTier;
  sitemapType?: SitemapType;
  segmentation?: SegmentationConfig;
}

export interface GenerateSitemapResponse {
  success: boolean;
  message: string;
  sitemapUrl: string;
  sitemaps: { filename: string; url: string; urlCount: number }[];
  indexUrl?: string;
  totalUrls: number;
  invalidUrlCount: number;
}

export interface SitemapStatus {
  domain: string;
  status: string;
  sitemaps: Sitemap[];
  summary: {
    totalSitemaps: number;
    totalUrls: number;
    totalSize: number;
    lastUpdated: string | null;
  };
}

export interface PingResults {
  google: { success: boolean; message: string };
  bing?: { success: boolean; message: string };
}

export interface UserInfo {
  user: {
    id: string;
    email: string;
    name: string;
    plan: string;
    createdAt: string;
  };
  usage: {
    domainsUsed: number;
    domainsLimit: number;
    urlsUsed: number;
    urlsLimit: number;
  };
}

export interface ApiKeyInfo {
  id: string;
  prefix: string;
  name: string;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface NewApiKey extends ApiKeyInfo {
  key: string;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  plan: string;
  createdAt: string;
}

export interface PoolDomain {
  id: string;
  domain: string;
  url_budget: number;
  urls_used: number;
}

export interface PoolStatus {
  url_pool: number;
  allocated: number;
  available: number;
  used: number;
  domains: PoolDomain[];
}

export interface AuthInfo {
  authenticated: boolean;
  user: AuthUser | null;
  usage: {
    domainsUsed: number;
    domainsLimit: number;
    urlsUsed: number;
    urlsLimit: number;
  } | null;
  pool: PoolStatus | null;
  limits: {
    plan: string;
    max_domains: number;
    max_urls: number;
    url_pool: number;
    min_urls_per_domain: number;
    api_access: boolean;
  } | null;
}

export interface GSCStatus {
  success: boolean;
  connected: boolean;
  tokenExpiresAt?: string;
}

export interface GSCSite {
  siteUrl: string;
  permissionLevel: string;
}
